import {useState} from 'react';
import {DateObject} from 'react-multi-date-picker';
import {LocalizedDatePicker as DatePicker} from '../common/dates/LocalizedCalendar';
import {Alert, Box, Button, CircularProgress, Grid, Paper, Stack, TextField} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import {authAPI} from '../../services/AuthServices';
import {analyticsAPI} from '../../services/AnalyticsServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {useCurrency} from '../../hooks/useCurrency';
import {getApiErrorMessage} from '../../common/apiErrors';
import {PageHeader} from '../common/layout/PageHeader';
import {FinanceSummaryCard} from '../finances/FinanceSummaryCard';
import {getCurrentMonth, isValidAnalyticsPeriod} from './analyticsPeriod';

const AnalyticsContent = () => {
    const {t, i18n} = useAppTranslation();
    const {currency} = useCurrency();
    const [period, setPeriod] = useState(getCurrentMonth);
    const [draft, setDraft] = useState(period);
    const valid = isValidAnalyticsPeriod(draft);
    const query = analyticsAPI.useGetOrderAnalyticsQuery(period, {refetchOnMountOrArgChange: true});
    const data = query.currentData;
    const locale = i18n.resolvedLanguage || i18n.language;
    const money = new Intl.NumberFormat(locale, {style: 'currency', currency}).format(data?.totalAmount ?? 0);

    return (
        <Box>
            <PageHeader title={t('analytics:title')} subtitle={t('analytics:subtitle')}/>
            <Paper component="form" sx={{p: {xs: 2, md: 3}, mb: 3}} onSubmit={(event) => {
                event.preventDefault();
                if (!valid) return;
                if (draft.from === period.from && draft.to === period.to) void query.refetch();
                else setPeriod({...draft});
            }}>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems={{sm: 'flex-start'}}>
                    {(['from', 'to'] as const).map((field) => (
                        <DatePicker key={field} value={draft[field]} format="YYYY-MM-DD"
                            containerStyle={{width: '100%'}} calendarPosition="bottom-start"
                            onChange={(value) => {
                                const date = Array.isArray(value) ? value[0] : value;
                                if (date instanceof DateObject) setDraft({...draft, [field]: date.format('YYYY-MM-DD')});
                            }}
                            render={(_, openCalendar) => (
                                <TextField fullWidth label={t(field === 'from' ? 'analytics:from' : 'analytics:to')}
                                    value={draft[field].split('-').reverse().join('.')} error={!valid}
                                    placeholder="dd.MM.yyyy" InputLabelProps={{shrink: true}}
                                    inputProps={{maxLength: 10, inputMode: 'numeric', style: {cursor: 'default'}}}
                                    onClick={openCalendar}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        const iso = /^\d{2}\.\d{2}\.\d{4}$/.test(value) ? value.split('.').reverse().join('-') : value;
                                        setDraft({...draft, [field]: iso});
                                    }}/>
                            )}/>
                    ))}
                    <Button type="submit" variant="contained" disabled={!valid || query.isFetching} sx={{flexShrink: 0}}>{t('analytics:apply')}</Button>
                    <Button variant="outlined" sx={{flexShrink: 0}} onClick={() => {
                        const month = getCurrentMonth();
                        setDraft(month);
                        setPeriod(month);
                    }}>{t('analytics:current_month')}</Button>
                </Stack>
                {!valid && <Alert severity="warning" sx={{mt: 2}}>{t('analytics:invalid_period')}</Alert>}
            </Paper>
            {query.isError && <Alert severity="error" sx={{mb: 3}} action={<Button color="inherit" onClick={() => query.refetch()}>{t('analytics:retry')}</Button>}>
                {getApiErrorMessage(query.error, t)}
            </Alert>}
            <Grid container spacing={2.5} aria-busy={query.isFetching}>
                <Grid item xs={12} sm={6}>
                    <FinanceSummaryCard title={t('analytics:order_count')} value={data && !query.isError ? new Intl.NumberFormat(locale).format(data.orderCount) : '—'}
                        hint={t('analytics:count_hint')} icon={<ReceiptLongOutlinedIcon/>} loading={query.isFetching}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FinanceSummaryCard title={t('analytics:total_amount')} value={data && !query.isError ? money : '—'}
                        hint={t('analytics:amount_hint')} icon={<PaymentsOutlinedIcon/>} loading={query.isFetching}/>
                </Grid>
            </Grid>
        </Box>
    );
};

const Analytics = () => {
    const {t} = useAppTranslation();
    const auth = authAPI.useGetAuthContextQuery();

    if (auth.isLoading || auth.isFetching) return <Box sx={{display: 'grid', placeItems: 'center', py: 6}}><CircularProgress/></Box>;
    if (auth.isError) return <Alert severity="error" action={<Button color="inherit" onClick={() => auth.refetch()}>{t('analytics:retry')}</Button>}>
        {getApiErrorMessage(auth.error, t)}
    </Alert>;
    if (!auth.data?.features?.includes('analytics')) return <Box>
        <PageHeader title={t('analytics:title')}/>
        <Alert severity="info">{t('analytics:in_development')}</Alert>
    </Box>;

    return <AnalyticsContent/>;
};

export default Analytics;
